import { Fragment, type FunctionComponent, useEffect, useState } from 'react'

import type { Webhook } from 'src/types/webhook'

import { showAlert } from 'src/actions/alertsActions'
import { startLoading, stopLoading } from 'src/actions/loadingActions'

import useWebhooksQuery from '~/hooks/services/webhooks/queries/useWebhooksQuery'

import Banner from 'src/components/Common/Banner'
import Count from 'src/components/Common/Count'
import Section from 'src/components/Common/Section'
import Spacer from 'src/components/Common/Spacer'
import WebhookDrawer from 'src/components/Drawers/WebhookDrawer'
import WebhookTable from 'src/components/Tables/WebhookTable'

import { Iconly } from 'react-iconly'
import { IoCloseCircleOutline } from 'react-icons/io5'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const Webhooks: FunctionComponent = (): JSX.Element => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [filtered, setFiltered] = useState<Webhook[]>([])
  const [showSearch, setShowSearch] = useState<boolean>(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { data: webhooksData, isLoading: isLoadingWebhooks, error } = useWebhooksQuery()

  /* filter the webhooks by search term */
  const filter = (value: string) => {
    const searchTerm = value.toLowerCase()

    if (!Array.isArray(webhooks)) {
      setFiltered([])
      return
    }

    setFiltered(
      webhooks.filter(
        (webhook: Webhook) =>
          webhook.name?.toLowerCase().includes(searchTerm) ||
          webhook.url?.toLowerCase().includes(searchTerm) ||
          webhook.authType.toLowerCase().includes(searchTerm) ||
          webhook.id.toLowerCase().includes(searchTerm)
      )
    )
  }

  /* get the webhooks */
  useEffect(() => {
    dispatch(startLoading())

    if (webhooksData) {
      // Ensure webhooksData is an array
      const whArray = Array.isArray(webhooksData) ? webhooksData : []
      setWebhooks(whArray)
      setFiltered(whArray)
    }

    if (error) {
      dispatch(
        showAlert({
          type: 'error',
          message: 'We were unable to retrieve your webhooks.',
          component: 'Webhooks',
          error: error as Error
        })
      )
      dispatch(stopLoading())
    }
  }, [webhooksData, error, dispatch])

  /* stop loading when the webhooks have been processed */
  useEffect(() => {
    if (filtered.length > 0 || (webhooks.length === 0 && !isLoadingWebhooks)) {
      dispatch(stopLoading())
    }
  }, [filtered, webhooks.length, isLoadingWebhooks, dispatch])

  const handleEditWebhook = (webhook: Webhook) => {
    navigate(`/webhooks/${webhook.id}`)
  }

  return (
    <Fragment>
      <Section>
        <div className="my-5 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-1 mdlg:grid-cols-2 xl:grid-cols-4">
          <Count
            icon={<Iconly name="Activity" set="bold" className="h-full w-full text-th-warning" />}
            value={Array.isArray(webhooks) ? webhooks.length : 0}
            label="All Webhooks"
            className="cursor-pointer"
            onClick={() => setFiltered(Array.isArray(webhooks) ? webhooks : [])}
            active={true}
          />
        </div>
      </Section>
      <Section>
        <Banner>
          <Spacer className="hidden sm:block" />
          {showSearch ? (
            <Fragment>
              <input type="text" className="rounded-lg p-2 font-body text-sm" placeholder="Search" autoFocus onChange={(e) => filter(e.target.value)} />
              <IoCloseCircleOutline className="h-6 w-6 cursor-pointer" onClick={() => setShowSearch(false)} />
            </Fragment>
          ) : (
            <Iconly name="Search" set="light" className="cursor-pointer" onClick={() => setShowSearch(true)} />
          )}
        </Banner>
        <Spacer />
        <WebhookTable webhooks={filtered} onEditWebhook={handleEditWebhook} />
      </Section>

      <WebhookDrawer />
    </Fragment>
  )
}

export default Webhooks
